import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Space, 
  Upload,
  Image,
  Popconfirm,
  message, 
  Card
} from 'antd';
import { 
  DeleteOutlined, 
  PlusOutlined,
  UploadOutlined,
  EditOutlined 
} from '@ant-design/icons';
import api from "../../api";

const { Option } = Select;

const SettingList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0
  });

  const fetchSettings = async (page = 1, pageSize = 25) => {
    setLoading(true);
    try {
      const response = await api.get(`/panel/setting?page=${page}&per_page=${pageSize}`);
      setData(response.data.data);
      setPagination({
        current: response.data.meta.current_page,
        pageSize: response.data.meta.per_page,
        total: response.data.meta.total
      });
    } catch (error) {
      message.error('خطا در بارگیری داده‌ها');
      console.error('خطا در بارگیری تنظیمات:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettingById = async (id) => {
    try {
      const response = await api.get(`/panel/setting/${id}`);
      return response.data.data;
    } catch (error) {
      message.error('خطا در بارگیری جزئیات تنظیمات');
      console.error('خطا در بارگیری جزئیات تنظیمات:', error);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleTableChange = (pagination) => {
    fetchSettings(pagination.current, pagination.pageSize);
  };

  const showModal = (record = null) => {
    form.resetFields();
    if (record) {
      setEditingId(record.id);
      form.setFieldsValue({
        key: record.key,
        type: record.type,
        value: record.type === 'text' ? record.value : undefined
      });
    } else {
      setEditingId(null);
    }
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/panel/setting/${id}`);
      message.success('تنظیمات با موفقیت حذف شد');
      fetchSettings(pagination.current);
    } catch (error) {
      message.error('خطا در حذف تنظیمات');
      console.error('خطا در حذف تنظیمات:', error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const formData = new FormData();
      formData.append('key', values.key);
      
      if (values.type === 'file') {
        const file = values.value?.[0]?.originFileObj;
        if (file) {
          formData.append('value', file);
          formData.append('type', 'file');
        } else if (!editingId) {
          message.error('لطفاً یک فایل انتخاب کنید');
          return;
        }
      } else {
        formData.append('value', values.value);
        formData.append('type', 'text');
      }

      if (editingId) {
        formData.append('_method', 'PUT');
        await api.post(`/panel/setting/${editingId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        message.success('تنظیمات با موفقیت به روز شد');
      } else {
        await api.post('/panel/setting', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        message.success('تنظیمات با موفقیت اضافه شد');
      }
      
      setModalVisible(false);
      fetchSettings();
    } catch (error) {
      message.error(editingId ? 'خطا در به روز رسانی تنظیمات' : 'خطا در افزودن تنظیمات');
      console.error('خطا در ارسال تنظیمات:', error);
    }
  };

  const columns = [
    {
      title: 'شناسه',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'کلید',
      dataIndex: 'key',
      key: 'key',
    },
    {
      title: 'نوع',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'مقدار',
      dataIndex: 'value',
      key: 'value',
      render: (value, record) => {
        if (record.type === 'file') {
          return <Image 
            src={value} 
            alt={record.key}
            width={100}
            style={{ objectFit: 'cover' }}
          />;
        }
        return value;
      }
    },
    {
      title: 'تاریخ ایجاد',
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: 'عملیات',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => showModal(record)} 
          />
          <Popconfirm
            title="آیا مطمئن هستید که می‌خواهید این تنظیمات را حذف کنید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله"
            cancelText="خیر"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title="تنظیمات">
      <div style={{ padding: 12 }}>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
          >
            افزودن تنظیمات جدید
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
        />

        <Modal
          title={editingId ? "ویرایش تنظیمات" : "افزودن تنظیمات جدید"}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="key"
              label="کلید"
              rules={[{ required: true, message: 'لطفاً کلید را وارد کنید' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="type"
              label="نوع"
              rules={[{ required: true, message: 'لطفاً نوع را انتخاب کنید' }]}
            >
              <Select>
                <Option value="text">متن</Option>
                <Option value="file">فایل</Option>
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
            >
              {({ getFieldValue }) => {
                const type = getFieldValue('type');
                
                return type === 'file' ? (
                  <Form.Item
                    name="value"
                    label="فایل"
                    rules={[{ required: !editingId, message: 'لطفاً یک فایل انتخاب کنید' }]}
                    getValueFromEvent={(e) => {
                      if (Array.isArray(e)) {
                        return e;
                      }
                      return e?.fileList;
                    }}
                  >
                    <Upload
                      maxCount={1}
                      beforeUpload={() => false}
                      listType="picture"
                      accept="image/*"
                    >
                      <Button icon={<UploadOutlined />}>انتخاب فایل</Button>
                    </Upload>
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="value"
                    label="مقدار"
                    rules={[{ required: true, message: 'لطفاً مقدار را وارد کنید' }]}
                  >
                    <Input />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item>
              <Space>
                <Button rowKey="id"
          loading={loading} type="primary" htmlType="submit">
                  {editingId ? 'به روز رسانی' : 'ارسال'}
                </Button>
                <Button onClick={() => setModalVisible(false)}>
                  انصراف
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Card>
  );
};

export default SettingList;
